/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import CustomRadioGroup, { CustomRadioOption } from "@ui-components/RadioGroup/CustomRadioGroup";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/IconDelete_KE.svg";
import { 
  getRouteTablesAndNetworkInterfaces, 
  getRouteTablesAndNetworkInterfacesForCluster 
} from "../../request/request";
import { 
  getFilteredRoutingTables, 
  getFilteredNetworkInterfaces, 
  getEmptyItemsMessage 
} from "../../../../helpers/filterFunctions";
import { 
  extractRouteTables, 
  extractNetworkInterfaces, 
  extractVpcs,
  extractFullRouteTablesData,
  extractFullNetworkInterfacesData
} from "../../../../helpers/formattersShapedData";
import { 
  clearEniSelection,
  removeRoutingTable,
  removeEni,
  removeRouteTarget,
  clearAllRoutingTables,
  clearAllEnis,
  clearAllSelections,
  VRFFormData
} from "../../../../helpers/clearComponentStates";

const POLICY_SELECTION_OPTIONS: CustomRadioOption[] = [
  { value: "specific", label: "Select specific routing tables" },
  { value: "dynamic", label: "Dynamic detection with tag" },
];

const NEXT_HOP_SELECTION_OPTIONS: CustomRadioOption[] = [
  { value: "specific", label: "Select specific ENI" },
  { value: "dynamic", label: "Dynamic detection with tag" },
];


interface VRF {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[]; // Add routing tables array
  enis: string[]; // Add ENIs array
  expanded: boolean;
  selectedVpc?: string; // Add VPC selection back
  inboundId?: number | null; // Add inboundId for Telco mode updates
}

interface InboundContentProps {
  values: any;
  setValues: (v: any) => void;
  isTelco?: boolean;
  reflectorId?: number; // Add reflectorId prop
  clusterId?: number; // Add clusterId prop
  getAccessTokenSilently?: () => Promise<string>; // Add token function prop
}

const defaultVRF = (name: string): VRF => ({
  name,
  routeTargets: [],
  newRouteTarget: "",
  routingTable: "",
  nextHopEni: "",
  policySelection: "dynamic", // Default to dynamic for new VRFs
  nextHopSelection: "dynamic", // Default to dynamic for new VRFs
  routingTables: [], // Initialize empty array
  enis: [], // Initialize empty array
  expanded: true,
  selectedVpc: "", // Initialize VPC selection
  inboundId: null, // Initialize as null for new VRFs
});

const InboundContent: React.FC<InboundContentProps> = ({ 
  values, 
  setValues, 
  isTelco = false, 
  reflectorId, 
  clusterId, 
  getAccessTokenSilently 
}) => {
  // State for API data
  const [routingTableOptions, setRoutingTableOptions] = useState<string[]>([]);
  const [eniOptions, setEniOptions] = useState<string[]>([]);
  const [vpcOptions, setVpcOptions] = useState<string[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  
  // State for VPC focus tracking
  const [vpcFocused, setVpcFocused] = useState(false);
  const [vpcHovered, setVpcHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Store full API response data for filtering
  const [fullRouteTablesData, setFullRouteTablesData] = useState<any[]>([]);
  const [fullNetworkInterfacesData, setFullNetworkInterfacesData] = useState<any[]>([]);


  // Robust hover handlers
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setVpcHovered(true);
  };

  const handleMouseLeave = () => {
    // Use a small timeout to prevent flickering when moving between elements
    hoverTimeoutRef.current = setTimeout(() => {
      setVpcHovered(false);
    }, 100);
  };


  // VRF state for Telco mode - use passed values or create default
  const [vrfs, setVrfs] = useState<VRF[]>(() => {
    if (values.vrfs && values.vrfs.length > 0) {
      // Use the passed VRF values
      return values.vrfs;
    } else {
      // Create default VRF only if no values are passed
      return [defaultVRF("Default VRF")];
    }
  });

  // Reset hover state when scrolling
  useEffect(() => {
    const handleScroll = () => {
      setVpcHovered(false);
      setVpcFocused(false);
    };

    // Add scroll listener to the modal container
    const modalContainer = document.querySelector('[role="dialog"]') || document.body;
    modalContainer.addEventListener('scroll', handleScroll);
    
    return () => {
      modalContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Reset hover state when VPC changes or validation state changes
  useEffect(() => {
    setVpcHovered(false);
    setVpcFocused(false);
  }, [values.selectedVpc, vrfs.map(vrf => vrf.selectedVpc).join(',')]);

  // Reset hover state when validation state changes (routing tables or ENIs)
  useEffect(() => {
    setVpcHovered(false);
    setVpcFocused(false);
  }, [
    values.routingTables?.length,
    values.enis?.length,
    vrfs.map(vrf => vrf.routingTables?.length || 0).join(','),
    vrfs.map(vrf => vrf.enis?.length || 0).join(',')
  ]);

  // Reset hover state when clicking anywhere outside VPC inputs
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside any VPC input area
      if (!target.closest('[data-vpc-input]')) {
        setVpcHovered(false);
        setVpcFocused(false);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Fetch route tables and network interfaces from API
  useEffect(() => {
    const fetchOptions = async () => {
      if (!getAccessTokenSilently || (!reflectorId && !clusterId)) {
        return;
      }

      setIsLoadingOptions(true);
      try {
        const token = await getAccessTokenSilently();
        let response;

        if (clusterId) {
          // Fetch for cluster
          response = await getRouteTablesAndNetworkInterfacesForCluster(token, clusterId);
        } else if (reflectorId) {
          // Fetch for standalone reflector
          response = await getRouteTablesAndNetworkInterfaces(token, reflectorId);
        }

        if (response?.data) {
          // Store full data for filtering using centralized extractors
          const routeTablesData = extractFullRouteTablesData(response);
          const networkInterfacesData = extractFullNetworkInterfacesData(response);
          const vpcs = extractVpcs(response);

          // Store full data
          setFullRouteTablesData(routeTablesData);
          setFullNetworkInterfacesData(networkInterfacesData);
          setVpcOptions(vpcs);

          // Initially show all options (no VPC filter applied yet)
          const routeTables = extractRouteTables(response);
          const networkInterfaces = extractNetworkInterfaces(response);

          setRoutingTableOptions(routeTables);
          setEniOptions(networkInterfaces);
        }
      } catch (error) {
        console.error('Error fetching route tables and network interfaces:', error);
        // Fallback to empty arrays on error
        setRoutingTableOptions([]);
        setEniOptions([]);
        setVpcOptions([]);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [reflectorId, clusterId, getAccessTokenSilently]);

  // Update VRF state when values change (when rule details are loaded)
  useEffect(() => {
    if (isTelco && values.vrfs && values.vrfs.length > 0) {
      setVrfs(values.vrfs);
    }
  }, [values.vrfs, isTelco]);

  // Sync VRF state to parent (only when vrfs change, not when values change)
  useEffect(() => {
    if (isTelco) {
      // Update top-level selectedVpc from first VRF's selectedVpc
      const firstVrfVpc = vrfs.length > 0 ? vrfs[0].selectedVpc : '';
      
      // Only update if the VRF array has actually changed
      if (JSON.stringify(values.vrfs) !== JSON.stringify(vrfs)) {
      setValues({ 
        ...values, 
        vrfs,
        selectedVpc: firstVrfVpc
      });
      }
    }
  }, [vrfs, isTelco]);

  // Handle VPC prefilling when values change (for both Enterprise and Telco modes)
  useEffect(() => {
    if (values.selectedVpc && fullRouteTablesData.length > 0 && fullNetworkInterfacesData.length > 0) {
      // Update filtered options when VPC is pre-filled
      setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, values.selectedVpc));
      setEniOptions(getFilteredNetworkInterfaces(fullNetworkInterfacesData, values.selectedVpc));
    }
  }, [values.selectedVpc, fullRouteTablesData, fullNetworkInterfacesData]);

  // Handle VPC prefilling for Telco mode VRFs
  useEffect(() => {
    if (isTelco && vrfs.length > 0 && fullRouteTablesData.length > 0 && fullNetworkInterfacesData.length > 0) {
      // Check if any VRF has a selected VPC and update filtered options accordingly
      const vrfWithVpc = vrfs.find(vrf => vrf.selectedVpc);
      if (vrfWithVpc?.selectedVpc) {
        setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, vrfWithVpc.selectedVpc));
        setEniOptions(getFilteredNetworkInterfaces(fullNetworkInterfacesData, vrfWithVpc.selectedVpc));
      }
    }
  }, [isTelco, vrfs, fullRouteTablesData, fullNetworkInterfacesData]);

  // Helper function to add routing table to array
  const addRoutingTable = (routingTable: string, vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, add to specific VRF
      if (routingTable && !vrfs[vrfIndex].routingTables.includes(routingTable)) {
        setVrfs(vrfs.map((vrf, i) => 
          i === vrfIndex 
            ? { ...vrf, routingTables: [...vrf.routingTables, routingTable], selectedRoutingTable: "" }
            : vrf
        ));
      }
    } else {
      // For Enterprise mode, add to main values
      if (routingTable && !values.routingTables?.includes(routingTable)) {
        setValues({ 
          ...values, 
          routingTables: [...(values.routingTables || []), routingTable],
          selectedRoutingTable: "" // Clear selection after adding
        });
      }
    }
  };


  // Helper function to add ENI to array
  const addEni = (eni: string, vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, add to specific VRF
      if (eni && !vrfs[vrfIndex].enis.includes(eni)) {
        setVrfs(vrfs.map((vrf, i) => 
          i === vrfIndex 
            ? { ...vrf, enis: [...vrf.enis, eni], selectedEni: "" }
            : vrf
        ));
      }
    } else {
      // For Enterprise mode, add to main values
      if (eni && !values.enis?.includes(eni)) {
        setValues({ 
          ...values, 
          enis: [...(values.enis || []), eni],
          selectedEni: "" // Clear selection after adding
        });
      }
    }
  };


  // Helper function to add route target to VRF
  const addRouteTarget = (vrfIndex: number) => {
    const vrf = vrfs[vrfIndex];
    if (vrf.newRouteTarget.trim()) {
      const targets = vrf.newRouteTarget.split(',').map(rt => rt.trim()).filter(rt => rt);
      const newTargets = targets.filter(target => !vrf.routeTargets.includes(target));
      if (newTargets.length > 0) {
        setVrfs(vrfs.map((item, i) => 
          i === vrfIndex 
            ? { ...item, routeTargets: [...item.routeTargets, ...newTargets], newRouteTarget: "" }
            : item
        ));
      }
    }
  };


  // Helper function to get existing ENI values from API response
  const getExistingEniValues = (values: any, vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, get ENIs from specific VRF
      const vrf = values.vrfs?.[vrfIndex];
      if (vrf?.enis && vrf.enis.length > 0) {
        return [vrf.enis[0]]; // Return first ENI value
      }
    } else {
      // For Enterprise mode, get ENIs from main values
      if (values.enis && values.enis.length > 0) {
        return [values.enis[0]]; // Return first ENI value
      }
    }
    return [];
  };

  // Helper function to check if VPC can be changed (validation)
  const canChangeVpc = (vrfIndex?: number) => {
    if (isTelco && vrfIndex !== undefined) {
      // For Telco mode, check specific VRF
      const vrf = vrfs[vrfIndex];
      const hasRoutingTables = vrf.routingTables && vrf.routingTables.length > 0;
      const hasEnis = vrf.enis && vrf.enis.length > 0;
      return !hasRoutingTables && !hasEnis;
    } else {
      // For Enterprise mode, check main values
      const hasRoutingTables = values.routingTables && values.routingTables.length > 0;
      const hasEnis = values.enis && values.enis.length > 0;
      return !hasRoutingTables && !hasEnis;
    }
  };

  // Helper function to get validation message
  const getVpcValidationMessage = (vrfIndex?: number) => {
    // Only show validation message if VPC cannot be changed
    if (canChangeVpc(vrfIndex)) {
      return "";
    }
    
    if (isTelco && vrfIndex !== undefined) {
      const vrf = vrfs[vrfIndex];
      const hasRoutingTables = vrf.routingTables && vrf.routingTables.length > 0;
      const hasEnis = vrf.enis && vrf.enis.length > 0;
      
      if (hasRoutingTables && hasEnis) {
        return "Clear all routing tables and ENIs before changing VPC";
      } else if (hasRoutingTables) {
        return "Clear all routing tables before changing VPC";
      } else if (hasEnis) {
        return "Clear all ENIs before changing VPC";
      }
    } else {
      const hasRoutingTables = values.routingTables && values.routingTables.length > 0;
      const hasEnis = values.enis && values.enis.length > 0;
      
      if (hasRoutingTables && hasEnis) {
        return "Clear all routing tables and ENIs before changing VPC";
      } else if (hasRoutingTables) {
        return "Clear all routing tables before changing VPC";
      } else if (hasEnis) {
        return "Clear all ENIs before changing VPC";
      }
    }
    return "";
  };


  // Helper function to set existing ENI values when switching to specific mode
  const handleNextHopSelectionChange = (selection: string, vrfIndex?: number) => {
    if (selection === "specific") {
      const existingEnis = getExistingEniValues(values, vrfIndex);
      if (existingEnis.length > 0) {
        // Set the first existing ENI as selected
        const firstEni = existingEnis[0];
        if (isTelco && vrfIndex !== undefined) {
          setVrfs(vrfs.map((item, i) => 
            i === vrfIndex 
              ? { ...item, nextHopSelection: selection, selectedEni: firstEni, enis: [firstEni] }
              : item
          ));
        } else {
          setValues({ 
            ...values, 
            nextHopSelection: selection,
            selectedEni: firstEni,
            enis: [firstEni]
          });
        }
        return;
      }
    }
    
    // Default behavior for dynamic mode or no existing ENIs
    if (isTelco && vrfIndex !== undefined) {
      setVrfs(vrfs.map((item, i) => 
        i === vrfIndex 
          ? { ...item, nextHopSelection: selection, selectedEni: "", enis: [] }
          : item
      ));
    } else {
      setValues({ 
        ...values, 
        nextHopSelection: selection,
        selectedEni: "",
        enis: []
      });
    }
  };

  // Helper function to create policy selection options with body content
  const createPolicySelectionOptions = (
    vrf: VRF,
    idx: number,
    routingTableOptions: string[],
    isLoadingOptions: boolean,
    addRoutingTable: (routingTable: string, vrfIndex?: number) => void,
    removeRoutingTable: (routingTable: string, isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setVrfs: any,
    isTelco: boolean,
    values: any,
    setValues: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific routing tables",
      bodyContent: (
                    <div className="mt-4">
                      <div className="flex items-end gap-2 w-full mb-2">
                        <GenericSearchableDropdown
                          items={routingTableOptions || []}
                          value={String(vrf.selectedRoutingTable ?? "")}
                          onSelect={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, selectedRoutingTable: v } : item))}
                          defaultLabel="Select..."
                          widthClass="w-full"
                          className="flex-1"
                          minWidthClass=""
                          maxWidthClass=""
                          disabled={isLoadingOptions || !vrf.selectedVpc}
                          emptyItemsMessage={getEmptyItemsMessage(vrf.selectedVpc || "", isLoadingOptions, "No routing tables available")}
                          allowCustomValues={true}
                        />
                        <GenericButton
                          label="+"
                          customHeight="35px"
                          variant="secondary"
                          shape="ghost"
                          size="sm"
                          onClick={() => addRoutingTable(vrf.selectedRoutingTable || "", idx)}
                          disabled={!vrf.selectedRoutingTable}
                        />
                      </div>
                      {/* Display routing tables as tags */}
                      {vrf.routingTables.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {vrf.routingTables.map((routingTable, rtIdx) => (
                            <div key={rtIdx} className="flex items-center">
                              <GenericTag
                                label={routingTable}
                                variant="default"
                                removable={true}
                                onRemove={() => removeRoutingTable(routingTable, isTelco, idx, vrfs, setVrfs, values, setValues)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      bodyContent: (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <GenericInput
                        label="Routing table tag name"
                        value={vrf.routingTableTagName ?? ""}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, routingTableTagName: v } : item))}
                        placeholder="E.g. RT"
                        helperText="Name used for dynamic detection"
                        className="text-body"
                      />
                      <GenericInput
                        label="Routing table tag value"
                        value={vrf.routingTableTagValue ?? ""}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, routingTableTagValue: v } : item))}
                        placeholder="E.g. RT"
                        helperText="Value the tag should match"
                        className="text-body"
                      />
                    </div>
      )
    }
  ];

  // Helper function to create next hop selection options with body content
  const createNextHopSelectionOptions = (
    vrf: VRF,
    idx: number,
    eniOptions: string[],
    isLoadingOptions: boolean,
    getExistingEniValues: (values: any, vrfIndex?: number) => string[],
    clearEniSelection: (isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setVrfs: any,
    isTelco: boolean,
    values: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific ENI",
      bodyContent: (
        <div className="mt-4">
          <div className="flex items-end gap-2 w-full">
            <GenericSearchableDropdown
              items={eniOptions || []}
              value={(() => {
                const existingEnis = getExistingEniValues(values, idx);
                // If we have existing ENIs, use the first one as value; otherwise use current selection
                return existingEnis.length > 0 ? existingEnis[0] : String(vrf.selectedEni ?? "");
              })()}
              defaultLabel="Select..."
              onSelect={(v) => {
                // Automatically set the ENI when selected (only one ENI supported)
                setVrfs(vrfs.map((item, i) => i === idx ? { 
                  ...item, 
                  selectedEni: v,
                  enis: v ? [v] : [] // Set only one ENI
                } : item));
              }}
              widthClass="w-full"
              className="flex-1"
              minWidthClass=""
              maxWidthClass=""
              disabled={isLoadingOptions || !vrf.selectedVpc}
              emptyItemsMessage={getEmptyItemsMessage(vrf.selectedVpc || "", isLoadingOptions, "No network interfaces available")}
              allowCustomValues={true}
            />
            <GenericButton
              label="×"
              customHeight="35px"
              variant="secondary"
              shape="ghost"
              size="sm"
              onClick={() => clearEniSelection(isTelco, idx, vrfs, setVrfs, values, setValues)}
              disabled={!vrf.selectedEni && (!vrf.enis || vrf.enis.length === 0)}
            />
          </div>
        </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      bodyContent: (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <GenericInput
                        label="Next-hop tag name"
                        value={vrf.nextHopTagName ?? ""}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, nextHopTagName: v } : item))}
                        placeholder="E.g. RT"
                        helperText="Name used for dynamic detection"
                        className="text-body"
                      />
                      <GenericInput
                        label="Next-hop tag value"
                        value={vrf.nextHopTagValue ?? ""}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, nextHopTagValue: v } : item))}
                        placeholder="E.g. RT"
                        helperText="Value the tag should match"
                        className="text-body"
                      />
        </div>
      )
    }
  ];

  // Helper function to create policy selection options with body content for Enterprise mode
  const createEnterprisePolicySelectionOptions = (
    values: any,
    routingTableOptions: string[],
    isLoadingOptions: boolean,
    addRoutingTable: (routingTable: string, vrfIndex?: number) => void,
    removeRoutingTable: (routingTable: string, isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setValues: any,
    isTelco: boolean,
    vrfs: any,
    setVrfs: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific routing tables",
      bodyContent: (
        <div className="mt-4">
          <div className="flex items-end gap-2 w-full mb-2">
            <GenericSearchableDropdown
              items={routingTableOptions || []}
              value={String(values.selectedRoutingTable ?? "")}
              onSelect={(v) => setValues({ ...values, selectedRoutingTable: v })}
              defaultLabel="Select..."
              widthClass="w-full"
              className="flex-1"
              minWidthClass=""
              maxWidthClass=""
              disabled={isLoadingOptions || !values.selectedVpc}
              emptyItemsMessage={getEmptyItemsMessage(values.selectedVpc || "", isLoadingOptions, "No routing tables available")}
              allowCustomValues={true}
            />
            <GenericButton
              label="+"
              customHeight="35px"
              variant="secondary"
              shape="ghost"
              size="sm"
              onClick={() => addRoutingTable(values.selectedRoutingTable || "")}
              disabled={!values.selectedRoutingTable}
            />
          </div>
          {/* Display routing tables as tags */}
          {values.routingTables && values.routingTables.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {values.routingTables.map((routingTable: string, index: number) => (
                <div key={index} className="flex items-center">
                  <GenericTag
                    label={routingTable}
                    variant="default"
                    removable={true}
                    onRemove={() => removeRoutingTable(routingTable, isTelco, 0, vrfs, setVrfs, values, setValues)}
                  />
                </div>
              ))}
                    </div>
                  )}
        </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      bodyContent: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <GenericInput
            label="Routing table tag name"
            value={values.routingTableTagName ?? ""}
            onChange={(v) => setValues({ ...values, routingTableTagName: v })}
            placeholder="E.g. RT"
            helperText="Name used for dynamic detection"
            className="text-body"
          />
          <GenericInput
            label="Routing table tag value"
            value={values.routingTableTagValue ?? ""}
            onChange={(v) => setValues({ ...values, routingTableTagValue: v })}
            placeholder="E.g. RT"
            helperText="Value the tag should match"
            className="text-body"
          />
        </div>
      )
    }
  ];

  // Helper function to create next hop selection options with body content for Enterprise mode
  const createEnterpriseNextHopSelectionOptions = (
    values: any,
    eniOptions: string[],
    isLoadingOptions: boolean,
    getExistingEniValues: (values: any, vrfIndex?: number) => string[],
    clearEniSelection: (isTelco: boolean, vrfIndex: number, vrfs: VRFFormData[], setVrfs: any, values: any, setValues: any) => void,
    setValues: any,
    isTelco: boolean,
    vrfs: any,
    setVrfs: any
  ): CustomRadioOption[] => [
    {
      value: "specific",
      label: "Select specific ENI",
      bodyContent: (
                    <div className="mt-4">
                      <div className="flex items-end gap-2 w-full">
                        <GenericSearchableDropdown
                          items={eniOptions || []}
                          value={(() => {
                const existingEnis = getExistingEniValues(values);
                            // If we have existing ENIs, use the first one as value; otherwise use current selection
                return existingEnis.length > 0 ? existingEnis[0] : String(values.selectedEni ?? "");
                          })()}
                          defaultLabel="Select..."
                          onSelect={(v) => {
                            // Automatically set the ENI when selected (only one ENI supported)
                setValues({ 
                  ...values, 
                              selectedEni: v,
                              enis: v ? [v] : [] // Set only one ENI
                });
                          }}
                          widthClass="w-full"
                          className="flex-1"
                          minWidthClass=""
                          maxWidthClass=""
              disabled={isLoadingOptions || !values.selectedVpc}
              emptyItemsMessage={getEmptyItemsMessage(values.selectedVpc || "", isLoadingOptions, "No network interfaces available")}
                          allowCustomValues={true}
                        />
                        <GenericButton
                          label="×"
                          customHeight="35px"
                          variant="secondary"
                          shape="ghost"
                          size="sm"
              onClick={() => clearEniSelection(isTelco, 0, vrfs, setVrfs, values, setValues)}
              disabled={!values.selectedEni && (!values.enis || values.enis.length === 0)}
                        />
                      </div>
                    </div>
      )
    },
    {
      value: "dynamic",
      label: "Dynamic detection with tag",
      bodyContent: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <GenericInput
            label="Next-hop tag name"
            value={values.nextHopTagName ?? ""}
            onChange={(v) => setValues({ ...values, nextHopTagName: v })}
            placeholder="E.g. RT"
            helperText="Name used for dynamic detection"
            className="text-body"
          />
          <GenericInput
            label="Next-hop tag value"
            value={values.nextHopTagValue ?? ""}
            onChange={(v) => setValues({ ...values, nextHopTagValue: v })}
            placeholder="E.g. RT"
            helperText="Value the tag should match"
            className="text-body"
          />
        </div>
      )
    }
  ];

  // Telco logic
  if (isTelco) {
    return (
      <div className="flex flex-col gap-6 text-body">
        <div className="rounded-sm border border-semantic-border-secondary p-4 text-semantic-text-secondary text-body">
          Configure how IPv4 routes from this BGP session are propagated to cloud routing tables. Specify routing tables and next-hop preferences
        </div>
        {/* VRF configuration */}
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-semantic-text-primary text-body">VRF configuration</div>
          <GenericButton label="+ Add VRF" size="sm" variant="secondary" shape="ghost" onClick={() => setVrfs([...vrfs, defaultVRF(`Default VRF${vrfs.length > 0 ? ` ${vrfs.length + 1}` : ""}`)])} />
        </div>
        <div className="flex flex-col gap-4">
          {vrfs.map((vrf, idx) => {
            const vrfBody = (
              <div className="p-4 flex flex-col gap-4 ">
                <GenericInput
                  label="VRF name"
                  value={vrf.name}
                  onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, name: v } : item))}
                  className="text-body text-semantic-text-primary"
                />
                <div className="pb-5">
                  <div className=" text-semantic-text-primary mb-2 text-body">VPC</div>
                  <div className="100vw">
                    <GenericTooltip
                      text={getVpcValidationMessage(idx)}
                      width="100vw"
                      triggerWidthClass="w-full"
                      position="bottom"
                      customOffset={{ left: -150 }}
                      isVisible={(vpcFocused || vpcHovered) && getVpcValidationMessage(idx) !== ""}
                    >
                    <div 
                      className="w-full"
                      data-vpc-input
                      onFocus={() => setVpcFocused(true)}
                      onBlur={() => setVpcFocused(false)}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <GenericSearchableDropdown
                        items={vpcOptions || []}
                        value={String(vrf.selectedVpc ?? "")}
                        onSelect={(v) => {
                          if (canChangeVpc(idx)) {
                            setVrfs(vrfs.map((item, i) => i === idx ? { ...item, selectedVpc: v } : item));
                            // Update filtered options when VPC changes
                            setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, v));
                            setEniOptions(getFilteredNetworkInterfaces(fullNetworkInterfacesData, v));
                            // Also update the top-level selectedVpc for payload building
                            setValues({ ...values, selectedVpc: v });
                          }
                        }}
                        defaultLabel="Select VPC..."
                        widthClass="w-full"
                        className="w-full"
                        minWidthClass=""
                        maxWidthClass=""
                        disabled={isLoadingOptions || !canChangeVpc(idx)}
                        emptyItemsMessage={isLoadingOptions ? "Loading..." : "No VPCs available"}
                        allowCustomValues={true}
                      />
                    </div>
                    </GenericTooltip>
                  </div>
                </div>
                <div>
                  <div className="text-body text-semantic-text-primary mb-1">Route targets</div>
                  <div className=" gap-2 mb-2 flex flex-col">
                    <div className="flex items-end gap-2">
                      <GenericInput
                        value={vrf.routeTargets.length > 0 ? vrf.routeTargets[0] : vrf.newRouteTarget}
                        onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { 
                          ...item, 
                          newRouteTarget: v,
                          routeTargets: v ? [v] : [] // Update the routeTargets array with the new value
                        } : item))}
                        placeholder="E.g. 65000:100"
                        className="text-body w-full flex-1"
                      />
                      {/* <GenericButton
                        label="+"
                        customHeight="35px"
                        variant="secondary"
                        shape="ghost"
                        size="sm"
                        onClick={() => addRouteTarget(idx)}
                        disabled={!vrf.newRouteTarget.trim()}
                      /> */}
                    </div>
                    {/* <span className=" ml-2 text-semantic-text-secondary text-xs">Comma-separate list of route targets</span> */}
                  </div>
                  {/* <div className="flex flex-wrap gap-2">
                    {vrf.routeTargets.map((rt, rtIdx) => (
                      <div key={rtIdx} className="flex items-center">
                        <GenericTag
                          label={rt}
                          variant="default"
                          removable={true}
                          onRemove={() => removeRouteTarget(idx, rtIdx, vrfs, setVrfs)}
                        />
                      </div>
                    ))}
                  </div> */}
                </div>
                <div>
                  <div className=" text-semantic-text-primary mb-2 text-body">Routing tables</div>
                  <CustomRadioGroup
                    name={`policySelection-${idx}`}
                    options={createPolicySelectionOptions(
                      vrf,
                      idx,
                      routingTableOptions,
                      isLoadingOptions,
                      addRoutingTable,
                      removeRoutingTable,
                      setVrfs,
                      isTelco,
                      values,
                      setValues
                    )}
                    value={vrf.policySelection}
                    onChange={(v) => setVrfs(vrfs.map((item, i) => i === idx ? { ...item, policySelection: v } : item))}
                  />
                </div>
                <div>
                  <div className=" text-semantic-text-primary mb-2 text-body">Next-hop settings</div>
                  <CustomRadioGroup
                    name={`nextHopSelection-${idx}`}
                    options={createNextHopSelectionOptions(
                      vrf,
                      idx,
                      eniOptions,
                      isLoadingOptions,
                      getExistingEniValues,
                      clearEniSelection,
                      setVrfs,
                      isTelco,
                      values
                    )}
                    value={vrf.nextHopSelection}
                    onChange={(v) => handleNextHopSelectionChange(v, idx)}
                  />
                </div>
              </div>
            );
            return (
              <GenericCard
                variant="primary"
                shadow="flat"
                key={idx}
                isCollapsible
                hasBorder={true}
                title={vrf.name}
                headerContent={vrf.name || "Default VRF"}
                bodyContent={vrfBody}
                initialExpanded={false}
                rightContent={vrfs.length > 1 ? (
                  <GenericButton
                    isIconOnly
                    icon={<IconDelete className="w-4 h-4" />}
                    iconPosition="right"
                    size="sm"
                    variant="danger"
                    shape="filled"
                    onClick={() => setVrfs(vrfs.filter((_, i) => i !== idx))}
                  />
                ) : undefined}
                actionsContent={true}
              />
            );
          })}
        </div>
      </div>
    );
  }


  // Enterprise logic
  return (
    <div className="flex flex-col gap-6 text-body">
      <div className="rounded-sm border border-semantic-border-secondary p-4 text-semantic-text-secondary text-body">
        Configure how IPv4 routes from this BGP session are propagated to cloud routing tables. Specify routing tables and next-hop preferences
      </div>
      <div className="pb-4">
        <div className=" text-semantic-text-primary mb-2 text-body">VPC</div>
        <div className="100vw">
        <GenericTooltip
          text={getVpcValidationMessage()}
          width="100vw"
          triggerWidthClass="w-full"
          position="bottom"
          customOffset={{ left: -180 }}
          isVisible={(vpcFocused || vpcHovered) && getVpcValidationMessage() !== ""}
        >
          <div 
            className="w-full"
            data-vpc-input
            onFocus={() => setVpcFocused(true)}
            onBlur={() => setVpcFocused(false)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <GenericSearchableDropdown
              items={vpcOptions || []}
              value={String(values.selectedVpc ?? "")}
              onSelect={(v) => {
                if (canChangeVpc()) {
                  setValues({ ...values, selectedVpc: v });
                  // Update filtered options when VPC changes
                  setRoutingTableOptions(getFilteredRoutingTables(fullRouteTablesData, v));
                  setEniOptions(getFilteredNetworkInterfaces(fullNetworkInterfacesData, v));
                }
              }}
              defaultLabel="Select VPC..."
              widthClass="w-full"
              className="w-full"
              minWidthClass=""
              maxWidthClass=""
              disabled={isLoadingOptions || !canChangeVpc()}
              emptyItemsMessage={isLoadingOptions ? "Loading..." : "No VPCs available"}
              allowCustomValues={true}
            />
          </div>
        </GenericTooltip>
        </div>
        
      </div>
      <div>
        <div className=" text-semantic-text-primary mb-2 text-body">Routing Tables</div>
        <CustomRadioGroup
          name="policySelection"
          options={createEnterprisePolicySelectionOptions(
            values,
            routingTableOptions,
            isLoadingOptions,
            addRoutingTable,
            removeRoutingTable,
            setValues,
            isTelco,
            vrfs,
            setVrfs
          )}
          value={values.policySelection}
          onChange={(v) => setValues({ ...values, policySelection: v })}
        />
      </div>
      <div>
        <div className=" text-semantic-text-primary mb-2 text-body">Next-hop settings</div>
        <CustomRadioGroup
          name="nextHopSelection"
          options={createEnterpriseNextHopSelectionOptions(
            values,
            eniOptions,
            isLoadingOptions,
            getExistingEniValues,
            clearEniSelection,
            setValues,
            isTelco,
            vrfs,
            setVrfs
          )}
          value={values.nextHopSelection}
          onChange={(v) => handleNextHopSelectionChange(v)}
        />
      </div>
    </div>
  );
};

export default InboundContent;
